/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import * as React from 'react';
import classNames from 'classnames';
import {
  updateSharingOption,
  attemptToPublish,
  resetUploadState,
  encodeSanitizedProfile,
} from 'firefox-profiler/actions/publish';
import {
  getProfile,
  getProfileRootRange,
  getHasPreferenceMarkers,
  getContainsPrivateBrowsingInformation,
  getHasJSTracingArgumentValues,
} from 'firefox-profiler/selectors/profile';
import {
  getAbortFunction,
  getCheckedSharingOptions,
  getFilenameString,
  getUploadPhase,
  getUploadProgress,
  getUploadProgressString,
  getUploadError,
  getShouldSanitizeByDefault,
  getSanitizedProfileEncodingState,
} from 'firefox-profiler/selectors/publish';
import { BlobUrlLink } from 'firefox-profiler/components/shared/BlobUrlLink';
import { assertExhaustiveCheck } from 'firefox-profiler/utils/types';
import prettyBytes from 'firefox-profiler/utils/pretty-bytes';

import explicitConnect, {
  type ConnectedProps,
} from 'firefox-profiler/utils/connect';

import type {
  Profile,
  CheckedSharingOptions,
  SharingMode,
  StartEndRange,
  UploadPhase,
  SanitizedProfileEncodingState,
} from 'firefox-profiler/types';

import './Publish.css';
import { Localized } from '@fluent/react';

type OwnProps = {
  readonly mode: SharingMode;
  readonly isRepublish?: boolean;
};

type StateProps = {
  readonly profile: Profile;
  readonly rootRange: StartEndRange;
  readonly shouldShowPreferenceOption: boolean;
  readonly profileContainsPrivateBrowsingInformation: boolean;
  readonly profileHasJSTracingArgumentValues: boolean;
  readonly checkedSharingOptions: CheckedSharingOptions;
  readonly sanitizedProfileEncodingState: SanitizedProfileEncodingState;
  readonly downloadFileName: string;
  readonly uploadPhase: UploadPhase;
  readonly uploadProgress: number;
  readonly uploadProgressString: string;
  readonly shouldSanitizeByDefault: boolean;
  readonly uploadError: unknown;
  readonly abortFunction: () => void;
};

type DispatchProps = {
  readonly updateSharingOption: typeof updateSharingOption;
  readonly encodeSanitizedProfile: typeof encodeSanitizedProfile;
  readonly attemptToPublish: typeof attemptToPublish;
  readonly resetUploadState: typeof resetUploadState;
};

type PublishProps = ConnectedProps<OwnProps, StateProps, DispatchProps>;

class PublishPanelImpl extends React.PureComponent<PublishProps, {}> {
  override componentDidMount(): void {
    this.props.encodeSanitizedProfile(this.props.mode);
  }

  _onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sharingOption = e.target.name as keyof CheckedSharingOptions;
    this.props.updateSharingOption(
      this.props.mode,
      sharingOption,
      e.target.checked
    );

    this.props.encodeSanitizedProfile(this.props.mode);
  };

  _renderCheckbox(
    slug: keyof CheckedSharingOptions,
    labelL10nId: string,
    additionalContent?: React.ReactNode
  ) {
    const { checkedSharingOptions, uploadPhase } = this.props;
    const isUploading =
      uploadPhase === 'uploading' || uploadPhase === 'compressing';
    return (
      <label className="photon-label publishPanelDataChoicesLabel">
        <input
          type="checkbox"
          className="photon-checkbox photon-checkbox-default"
          name={slug}
          onChange={this._onCheckboxChange}
          checked={checkedSharingOptions[slug]}
          disabled={isUploading}
        />
        <span>
          <Localized id={labelL10nId} />
          {additionalContent}
        </span>
      </label>
    );
  }

  _onSubmit = () => {
    this.props.attemptToPublish();
  };

  _renderPublishPanel() {
    const {
      shouldShowPreferenceOption,
      profileContainsPrivateBrowsingInformation,
      profileHasJSTracingArgumentValues,
      sanitizedProfileEncodingState,
      downloadFileName,
      shouldSanitizeByDefault,
      isRepublish,
      mode,
    } = this.props;

    const isDownload = mode === 'download';

    let title;
    if (isDownload) {
      title = (
        <Localized id="MenuButtons--publish--download-performance-profile">
          Download Performance Profile
        </Localized>
      );
    } else if (isRepublish) {
      title = (
        <Localized id="MenuButtons--publish--reshare-performance-profile">
          Re-share Performance Profile
        </Localized>
      );
    } else {
      title = (
        <Localized id="MenuButtons--publish--share-performance-profile">
          Share Performance Profile
        </Localized>
      );
    }

    return (
      <div data-testid="PublishPanel-container">
        <form
          className="publishPanelContent photon-body-10"
          onSubmit={isDownload ? undefined : this._onSubmit}
        >
          <h1 className="publishPanelTitle photon-title-30">{title}</h1>
          <p className="publishPanelInfoDescription">
            {isDownload ? (
              <Localized id="MenuButtons--publish--download-info-description">
                Save this profile as a file on your computer.
              </Localized>
            ) : (
              <Localized id="MenuButtons--publish--info-description">
                Upload your profile and make it accessible to anyone with the
                link.
              </Localized>
            )}{' '}
            {shouldSanitizeByDefault ? (
              <Localized id="MenuButtons--publish--info-description-default">
                By default, your personal data is removed.
              </Localized>
            ) : (
              <Localized id="MenuButtons--publish--info-description-firefox-nightly2">
                This profile is from Firefox Nightly, so by default most
                information is included.
              </Localized>
            )}
          </p>
          <h3 className="photon-title-10">
            <Localized id="MenuButtons--publish--include-additional-data">
              Include additional data that may be identifiable
            </Localized>
          </h3>
          <div className="publishPanelDataChoices">
            {this._renderCheckbox(
              'includeHiddenThreads',
              'MenuButtons--publish--renderCheckbox-label-hidden-threads'
            )}
            {this._renderCheckbox(
              'includeFullTimeRange',
              'MenuButtons--publish--renderCheckbox-label-hidden-time'
            )}
            {this._renderCheckbox(
              'includeScreenshots',
              'MenuButtons--publish--renderCheckbox-label-include-screenshots'
            )}
            {this._renderCheckbox(
              'includeUrls',
              'MenuButtons--publish--renderCheckbox-label-resource'
            )}
            {this._renderCheckbox(
              'includeExtension',
              'MenuButtons--publish--renderCheckbox-label-extension'
            )}
            {shouldShowPreferenceOption
              ? this._renderCheckbox(
                  'includePreferenceValues',
                  'MenuButtons--publish--renderCheckbox-label-preference'
                )
              : null}
            {profileContainsPrivateBrowsingInformation
              ? this._renderCheckbox(
                  'includePrivateBrowsingData',
                  'MenuButtons--publish--renderCheckbox-label-private-browsing',
                  <WarningIndicator
                    l10nId="MenuButtons--publish--renderCheckbox-label-private-browsing-warning-image"
                    title="This profile contains private browsing data"
                  />
                )
              : null}
            {profileHasJSTracingArgumentValues
              ? this._renderCheckbox(
                  'includeArgumentValues',
                  'MenuButtons--publish--renderCheckbox-label-argument-values',
                  <WarningIndicator
                    l10nId="MenuButtons--publish--renderCheckbox-label-argument-values-warning-image"
                    title="This profile contains function argument values recorded from the page, which may include personal data"
                  />
                )
              : null}
          </div>
          {sanitizedProfileEncodingState.phase === 'ERROR' ? (
            <div className="photon-message-bar photon-message-bar-error photon-message-bar-inner-content">
              <div className="photon-message-bar-inner-text">
                <Localized id="MenuButtons--publish--error-while-compressing">
                  Error while compressing, try unchecking some checkboxes to
                  reduce the profile size.
                </Localized>
              </div>
            </div>
          ) : null}
          <div className="publishPanelButtons">
            {isDownload ? (
              <DownloadButton
                primary
                downloadFileName={downloadFileName}
                sanitizedProfileEncodingState={sanitizedProfileEncodingState}
              />
            ) : (
              <button
                type="submit"
                className="photon-button photon-button-primary publishPanelButton publishPanelButtonsUpload"
                disabled={sanitizedProfileEncodingState.phase === 'ERROR'}
              >
                <span className="publishPanelButtonsSvg publishPanelButtonsSvgUpload" />
                <Localized id="MenuButtons--publish--button-upload">
                  Upload
                </Localized>{' '}
                {sanitizedProfileEncodingState.phase === 'DONE' ? (
                  <span className="menuButtonsDownloadSize">
                    (
                    {prettyBytes(
                      sanitizedProfileEncodingState.profileData.size
                    )}
                    )
                  </span>
                ) : null}
              </button>
            )}
          </div>
        </form>
      </div>
    );
  }

  _renderUploadPanel() {
    const {
      uploadProgress,
      uploadProgressString,
      abortFunction,
      downloadFileName,
      sanitizedProfileEncodingState,
    } = this.props;

    return (
      <div
        className="publishPanelUpload photon-body-10"
        data-testid="PublishPanel-container"
      >
        <div className="publishPanelUploadTop">
          <div className="publishPanelUploadTitle photon-title-20">
            <Localized id="MenuButtons--publish--upload-title">
              Uploading profile…
            </Localized>
          </div>
          <div className="publishPanelUploadPercentage">
            {uploadProgressString}
          </div>
          <div className="publishPanelUploadBar">
            <div
              className="publishPanelUploadBarInner"
              style={{ width: `${uploadProgress * 100}%` }}
            />
          </div>
        </div>
        <div className="publishPanelButtons">
          <DownloadButton
            downloadFileName={downloadFileName}
            sanitizedProfileEncodingState={sanitizedProfileEncodingState}
          />
          <button
            type="button"
            className="photon-button photon-button-default publishPanelButton publishPanelButtonsCancelUpload"
            onClick={abortFunction}
          >
            <Localized id="MenuButtons--publish--cancel-upload">
              Cancel Upload
            </Localized>
          </button>
        </div>
      </div>
    );
  }

  _renderErrorPanel() {
    const { uploadError: error, resetUploadState } = this.props;
    let message: string =
      'There was an unknown error when trying to upload the profile.';
    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
    ) {
      // This is most likely an error, but do a runtime check just in case.
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    }

    return (
      <div
        className="publishPanelUpload photon-body-10"
        data-testid="PublishPanel-container"
      >
        <div className="photon-message-bar photon-message-bar-error photon-message-bar-inner-content">
          <div className="photon-message-bar-inner-text">
            <Localized id="MenuButtons--publish--message-something-went-wrong">
              Uh oh, something went wrong when uploading the profile.
            </Localized>
          </div>
          <button
            className="photon-button photon-button-micro photon-message-bar-action-button"
            type="button"
            onClick={resetUploadState}
          >
            <Localized id="MenuButtons--publish--message-try-again">
              Try again
            </Localized>
          </button>
        </div>
        <div className="publishPanelError">{message}</div>
      </div>
    );
  }

  override render() {
    const { uploadPhase, mode } = this.props;
    if (mode === 'download') {
      return this._renderPublishPanel();
    }
    switch (uploadPhase) {
      case 'error':
        return this._renderErrorPanel();
      case 'local':
      case 'uploaded':
        return this._renderPublishPanel();
      case 'uploading':
      case 'compressing':
        return this._renderUploadPanel();
      default:
        throw assertExhaustiveCheck(uploadPhase);
    }
  }
}

export const PublishPanel = explicitConnect<
  OwnProps,
  StateProps,
  DispatchProps
>({
  mapStateToProps: (state, ownProps) => ({
    profile: getProfile(state),
    rootRange: getProfileRootRange(state),
    shouldShowPreferenceOption: getHasPreferenceMarkers(state),
    profileContainsPrivateBrowsingInformation:
      getContainsPrivateBrowsingInformation(state),
    profileHasJSTracingArgumentValues: getHasJSTracingArgumentValues(state),
    checkedSharingOptions: getCheckedSharingOptions(state, ownProps.mode),
    downloadFileName: getFilenameString(state),
    sanitizedProfileEncodingState: getSanitizedProfileEncodingState(state),
    uploadPhase: getUploadPhase(state),
    uploadProgress: getUploadProgress(state),
    uploadProgressString: getUploadProgressString(state),
    uploadError: getUploadError(state),
    shouldSanitizeByDefault: getShouldSanitizeByDefault(state),
    abortFunction: getAbortFunction(state),
  }),
  mapDispatchToProps: {
    updateSharingOption,
    encodeSanitizedProfile,
    attemptToPublish,
    resetUploadState,
  },
  component: PublishPanelImpl,
});

type DownloadButtonProps = {
  readonly sanitizedProfileEncodingState: SanitizedProfileEncodingState;
  readonly downloadFileName: string;
  readonly primary?: boolean;
};

type WarningIndicatorProps = {
  readonly l10nId: string;
  readonly title: string;
};

function WarningIndicator({ l10nId, title }: WarningIndicatorProps) {
  return (
    <Localized id={l10nId} attrs={{ title: true }}>
      <span className="publishPanelDataChoicesIndicator" title={title} />
    </Localized>
  );
}

/**
 * The DownloadButton handles unpacking the compressed profile promise.
 */
class DownloadButton extends React.PureComponent<DownloadButtonProps, {}> {
  override render() {
    const { sanitizedProfileEncodingState, downloadFileName, primary } =
      this.props;
    const className = classNames(
      'photon-button publishPanelButton publishPanelButtonsDownload',
      { 'photon-button-primary publishPanelButtonsDownloadPrimary': primary }
    );

    switch (sanitizedProfileEncodingState.phase) {
      case 'DONE': {
        const { profileData } = sanitizedProfileEncodingState;
        return (
          <BlobUrlLink
            blob={profileData}
            download={`${downloadFileName}.gz`}
            className={className}
          >
            <span className="publishPanelButtonsSvg publishPanelButtonsSvgDownload" />
            <Localized id="MenuButtons--publish--download">Download</Localized>{' '}
            <span className="menuButtonsDownloadSize">
              ({prettyBytes(profileData.size)})
            </span>
          </BlobUrlLink>
        );
      }
      case 'ERROR': {
        return (
          <button type="button" className={className} disabled>
            <Localized id="MenuButtons--publish--download">Download</Localized>
          </button>
        );
      }
      case 'INITIAL':
      case 'ENCODING': {
        return (
          <button
            type="button"
            className={classNames(className, 'publishPanelButtonDisabled')}
          >
            <Localized id="MenuButtons--publish--compressing">
              Compressing…
            </Localized>
          </button>
        );
      }
      default:
        throw assertExhaustiveCheck(sanitizedProfileEncodingState);
    }
  }
}
